import { Cancel, Close, Send } from '@mui/icons-material';
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  GenSelectTree,
  GenButton,
} from '@pinnacleautomotive/clarity-components';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import {
  CompanyStatus,
  UserRoles,
  UserRolesId,
} from '../../../common/enums/enums';
import { EMAIL_REGEXP } from '../../../helpers/regexpHelpers';
import { useGetDepartmentsByUserQuery } from '../../../redux/rtk/departments.services';
import {
  currentUserCompanyId,
  selectUserCompanies,
} from '../../../redux/selectors/userDataSelectors';
import PhoneInput from './PhoneInput';

const deleteIcon = <Cancel onMouseDown={event => event.stopPropagation()} />;

const multiSelectProps = {
  multiple: true,
  MenuProps: {
    sx: { maxHeight: '200px' },
  },
};

function DepartmentSelect({
  required,
  value,
  onChange,
  label,
  departmentsOfSelectedCompany,
}) {
  return (
    <FormControl fullWidth size="small" required={required}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={onChange}
        data-testid="orderingSelect"
        {...multiSelectProps}
        renderValue={() => (
          <Stack gap={0.5} direction="row" flexWrap="wrap">
            <Chip
              size="small"
              onDelete={() => onChange({ target: { value: [] } })}
              deleteIcon={deleteIcon}
              label={`${value.length} selected`}
            />
          </Stack>
        )}
      >
        {departmentsOfSelectedCompany.map(department => (
          <MenuItem key={department} value={department}>
            {department}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function EditExtraData({ register, control, errors }) {
  const { t } = useTranslation('users');

  const inputSx = { width: { sm: 'calc(50% - 12px)', xs: '100%' } };

  return (
    <Stack gap={3} pt={3}>
      <Divider />
      <Stack direction="row" gap={3} flexWrap="wrap">
        <TextField
          label={t('firstName')}
          required
          sx={inputSx}
          size="small"
          {...register('firstName', {
            required: true,
          })}
        />
        <TextField
          label={t('lastName')}
          required
          sx={inputSx}
          size="small"
          {...register('lastName', {
            required: true,
          })}
        />
        <Controller
          control={control}
          name="phone"
          rules={{
            required: false,
            validate: value => !value || isValidPhoneNumber(value),
          }}
          render={({ field: { value, onChange } }) => (
            <PhoneInput
              value={value}
              onChange={onChange}
              error={!!errors.phone}
              label={t('table.phone')}
              sx={inputSx}
            />
          )}
        />
        <TextField
          label={t('jobTitle')}
          sx={inputSx}
          size="small"
          {...register('title')}
        />
      </Stack>
    </Stack>
  );
}

export default function NewUserDialog({
  open,
  onClose,
  user,
  isLoading,
  onSave,
}) {
  const { t } = useTranslation('users');
  const companiesData = useSelector(selectUserCompanies);
  const companyId = useSelector(currentUserCompanyId);
  const [selectedCompany, setSelectedCompany] = useState(companyId);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const { data: departments } = useGetDepartmentsByUserQuery(selectedCompany);
  const theme = useTheme();
  const companies = useMemo(() => {
    return (
      companiesData?.filter(
        company => company.companyStatus === CompanyStatus.ACTIVE
      ) || []
    );
  }, [companiesData]);

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { isValid, isDirty, errors },
  } = useForm({
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      roleIds: [],
      companyId,
      email: '',
      approverOf: [],
      departments: [],
    },
  });

  useEffect(() => {
    if (!user) return;
    const roleIds = user.roles.map(role => role.roleId);
    reset({
      email: user.email,
      companyId: user.company.companyId,
      roleIds,
      phone: user.phone,
      firstName: user.firstName,
      lastName: user.lastName,
      title: user.title,
      departments: user.departments
        ?.map(
          company =>
            company.departmentName ||
            company.departments.map(department => department.name)
        )
        .flat(),
      approverOf: user.approverRights
        ?.map(
          company =>
            company.departmentName ||
            company.departments.map(department => department.name)
        )
        .flat(),
    });
    setSelectedRoles(roleIds);
    setSelectedCompany(user.company.companyId);
  }, [reset, user]);

  useEffect(() => {
    if (!open) {
      reset();
      setSelectedRoles([]);
      setSelectedCompany(companyId);
    }
  }, [open, reset, companyId]);

  const isEditing = !!user;

  const titleByIsEditing = {
    true: t('editUser'),
    false: t('inviteUser'),
  };
  const confirmButtonTextByIsEditing = {
    true: t('global:genericButtons.saveChanges'),
    false: t('sendInvite'),
  };

  const iconByIsEditing = {
    true: <Send />,
    false: null,
  };

  const helperTextByIsEditing = {
    true: null,
    false: t('emailHelper'),
  };

  const title = titleByIsEditing[isEditing];
  const confirmButtonText = confirmButtonTextByIsEditing[isEditing];
  const endIcon = iconByIsEditing[isEditing];
  const helperText = helperTextByIsEditing[isEditing];

  const orderingDepartmentsIsRequired =
    selectedRoles.includes(UserRolesId.DEALERSHIP_MANAGER) ||
    selectedRoles.includes(UserRolesId.DEALERSHIP_EMPLOYEE);

  const approvingDepartmentsIsRequired = selectedRoles.includes(
    UserRolesId.DEALERSHIP_MANAGER
  );

  useEffect(() => {
    !approvingDepartmentsIsRequired && setValue('approverOf', []);
    !orderingDepartmentsIsRequired && setValue('departments', []);
  }, [approvingDepartmentsIsRequired, orderingDepartmentsIsRequired, setValue]);

  const onSaveWrapper = data => {
    const newUser = { ...data };
    newUser.roleIds.length === 1 &&
      newUser.roleIds[0] === UserRolesId.DEALERSHIP_ADMIN &&
      delete newUser.departments &&
      delete newUser.approverOf;
    onSave(newUser);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle sx={{ background: theme.palette.grey[50] }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2" flexGrow={1}>
            {title}
          </Typography>
          <IconButton
            disabled={isLoading}
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack pt={3} gap={3}>
          <Controller
            control={control}
            name="companyId"
            defaultValue={user?.company.companyId || ''}
            rules={{
              required: true,
              validate: value => !!value,
            }}
            render={({ field: { value } }) => (
              <GenSelectTree
                label={`${t('level')}*`}
                items={user ? companiesData : companies}
                parentField="parentCompanyId"
                idField="companyId"
                textField="name"
                selectedItemId={value}
                onChange={newLevel => {
                  setValue('companyId', newLevel);
                  setValue('approverOf', []);
                  setValue('departments', []);
                  setSelectedCompany(newLevel);
                }}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                }}
                size="small"
              />
            )}
          />
          <FormControl fullWidth size="small" required>
            <InputLabel>{t('table.role')}</InputLabel>
            <Controller
              control={control}
              name="roleIds"
              rules={{
                required: true,
                validate: value => !!value.length,
              }}
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t('table.role')}
                  {...multiSelectProps}
                  value={value}
                  onChange={e => {
                    onChange(e);
                    setSelectedRoles(e.target.value);
                  }}
                  data-testid="rolesSelect"
                  renderValue={() => (
                    <Stack gap={0.5} direction="row" flexWrap="wrap">
                      <Chip
                        size="small"
                        onDelete={() => {
                          setSelectedRoles([]);
                          onChange({ target: { value: [] } });
                        }}
                        deleteIcon={deleteIcon}
                        label={`${value.length} selected`}
                      />
                    </Stack>
                  )}
                >
                  <MenuItem value={UserRolesId.DEALERSHIP_ADMIN}>
                    {UserRoles.DEALERSHIP_ADMIN}
                  </MenuItem>
                  <MenuItem
                    value={UserRolesId.DEALERSHIP_MANAGER}
                    disabled={selectedRoles.includes(
                      UserRolesId.DEALERSHIP_EMPLOYEE
                    )}
                  >
                    {UserRoles.DEALERSHIP_MANAGER}
                  </MenuItem>
                  <MenuItem
                    value={UserRolesId.DEALERSHIP_EMPLOYEE}
                    disabled={selectedRoles.includes(
                      UserRolesId.DEALERSHIP_MANAGER
                    )}
                  >
                    {UserRoles.DEALERSHIP_EMPLOYEE}
                  </MenuItem>
                </Select>
              )}
            />
          </FormControl>

          <Controller
            control={control}
            name="departments"
            rules={{
              required: orderingDepartmentsIsRequired,
              validate: value =>
                !orderingDepartmentsIsRequired || !!value.length,
            }}
            render={({ field: { value, onChange } }) =>
              orderingDepartmentsIsRequired && (
                <DepartmentSelect
                  label={t('orderingDepartments')}
                  value={value}
                  onChange={onChange}
                  departmentsOfSelectedCompany={departments}
                  required={orderingDepartmentsIsRequired}
                />
              )
            }
          />

          <Controller
            control={control}
            name="approverOf"
            rules={{
              required: approvingDepartmentsIsRequired,
              validate: value =>
                !approvingDepartmentsIsRequired || !!value.length,
            }}
            render={({ field: { value, onChange } }) =>
              approvingDepartmentsIsRequired && (
                <DepartmentSelect
                  label={t('approvingDepartments')}
                  value={value}
                  onChange={onChange}
                  departmentsOfSelectedCompany={departments}
                  required={approvingDepartmentsIsRequired}
                />
              )
            }
          />
          <TextField
            label={t('table.email')}
            size="small"
            helperText={errors.email?.message || helperText}
            disabled={!!user?.email}
            data-testid="emailField"
            required
            {...register('email', {
              required: true,
              pattern: {
                value: EMAIL_REGEXP,
                message: t('emailError'),
              },
            })}
            error={!!errors.email}
          />
        </Stack>
        {isEditing && (
          <EditExtraData
            register={register}
            control={control}
            errors={errors}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <GenButton
          text={t('global:genericButtons.cancel')}
          order={0}
          disabled={isLoading}
          onClick={onClose}
          sx={{
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
          }}
        />
        <GenButton
          text={confirmButtonText}
          order={1}
          disabled={!isValid || !isDirty}
          endIcon={endIcon}
          color="primary"
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit(onSaveWrapper)}
        />
      </DialogActions>
    </Dialog>
  );
}
